import { api } from './client'
import { requireArray } from './readShape'

/**
 * Report schedules (spec 058).
 *
 * Unlike the two job endpoints, this one returns a single camelCase shape from
 * every route (cb-api's `toApi`), so there is no normalize/merge dance here.
 *
 * The one thing worth knowing: **recurrence previews come from the server**. The
 * editor could compute "the next three runs" locally, but then the preview and
 * the cron would be two implementations of the same calendar rules and would
 * eventually disagree — and a schedule that fires on a different day than the
 * preview promised is exactly the bug nobody reports until month-end. One engine,
 * server-side, debounced from the editor.
 */
const reportScheduleService = {
  /**
   * The schedules visible at `scope` (CC-50282).
   *
   * Returns the envelope, not a bare array, because `scopeResolved` is load-bearing:
   * `false` means the CB group lookup failed and the server narrowed to "mine", which
   * the page must SAY rather than render as an empty group. "Your group has nothing"
   * and "the lookup broke" are indistinguishable otherwise, and that ambiguity hid
   * the CC-49595 bug through two QA rounds.
   */
  list: async (scope = 'mine') => {
    const q = scope && scope !== 'mine' ? `?scope=${encodeURIComponent(scope)}` : ''
    const data = await api.get(`/reports/schedules${q}`)
    const d = data?.data || data || {}
    return {
      // CC-53052 — the comment directly above invokes this exact bug class by name, and
      // then the next line was `d.schedules || []`. It solved the SCOPE ambiguity via
      // `scopeResolved` and left the SHAPE one wide open: an unreadable body rendered
      // "no schedules", which for a feature whose whole point is unattended delivery
      // reads as "nothing is scheduled" — the most reassuring possible lie.
      schedules: requireArray(d.schedules, { noun: 'schedule list', emptyNoun: 'schedule list' }),
      scope: d.scope || scope,
      // Absent on an older worker ⇒ treat as resolved rather than warning falsely.
      scopeResolved: d.scopeResolved !== false,
      // Spec 152 — the results bucket's age limit in days, for the editor's "Keep
      // results" sentence. Null when the worker does not declare one: no age is stated.
      resultMaxAgeDays: d.resultMaxAgeDays ?? null,
      // Spec 169 — this list includes colleagues' PRIVATE schedules because CB confirmed,
      // on this request, that the reader is an administrator. Literal true only; absent
      // (an older worker) is false, which is also what that worker shows.
      adminView: d.adminView === true,
    }
  },

  get: async (id) => {
    const data = await api.get(`/reports/schedules/${id}`)
    return data?.data || data
  },

  /*
   * `payload` passes through whole. Spec 138: it may carry `deliverRecipients`, the
   * schedule's OTHER addresses (up to 25 since spec 201, each validated by cb-api). On `update` the
   * list REPLACES the stored one, `[]` clears it, and leaving the key out keeps it.
   * The answer's `schedule.deliverRecipients` is owner-only: `[]` on a colleague's row.
   *
   * `config` (optional) is axios's request config — e.g. `{ timeout }` for spec 209's
   * Advanced Report door, which can read the user's Exago list (≤ 45 s, after a ≤ 5 s probe)
   * before it answers. Sent as `api.post`'s third argument ONLY when given, so every other
   * caller's request is exactly what it was.
   */
  create: async (payload, config) => {
    const data = config === undefined
      ? await api.post('/reports/schedules', payload)
      : await api.post('/reports/schedules', payload, config)
    return data?.data?.schedule || data?.schedule
  },

  update: async (id, payload) => {
    const data = await api.put(`/reports/schedules/${id}`, payload)
    return data?.data?.schedule || data?.schedule
  },

  remove: async (id) => api.delete(`/reports/schedules/${id}`),

  /**
   * Run this schedule now, IN ADDITION to its scheduled slot (spec 075).
   *
   * A dedicated route rather than a normal job create: the run has to reuse the
   * schedule's stored filters, roll-forward preset, delivery prefs and retention,
   * and rebuilding that here would be a second definition of what the schedule
   * means. Returns the new job id so the caller can offer a link to it.
   */
  runNow: async (id) => {
    const data = await api.post(`/reports/schedules/${id}/run`)
    return data?.data || data
  },

  /**
   * Skip exactly the next firing. The server advances the schedule to the
   * occurrence after it and returns both the updated schedule and the moment it
   * skipped, so the UI can say which run it dropped.
   */
  skipNext: async (id) => {
    const data = await api.post(`/reports/schedules/${id}/skip`)
    return data?.data || data
  },

  /**
   * Copy a schedule. Server-side, NOT a read-then-create here: a schedule's stored
   * filters never leave the server (`toApi` omits `params_json`), so a client-side
   * copy would post a schedule with no filters — and CB silently ignores unknown
   * properties, so the proc would run UNSCOPED with nothing reporting an error.
   * The copy arrives paused.
   */
  duplicate: async (id) => {
    const data = await api.post(`/reports/schedules/${id}/duplicate`)
    return data?.data?.schedule || data?.schedule
  },

  /**
   * Next firings for a candidate rule, plus the date window each run would
   * resolve to. `rollForward` is the preset TOKEN, so the preview shows the
   * window moving — which is the whole point of the control.
   */
  preview: async ({ recurrence, rollForward, filters, count = 3 }) => {
    // `filters` is sent so the server can answer whether the roll-forward token
    // will actually be STORED against this report, not just what window it
    // would resolve to (CC-50232 F1). Without it the preview happily rendered a
    // rolling window for a report with no date filter to stamp — and the saved
    // schedule then said `pinned`. Omitted ⇒ the server replies
    // `rollForwardApplied: null`, i.e. "not asked", and nothing is claimed.
    const data = await api.post('/reports/schedules/preview', {
      recurrence,
      rollForward,
      count,
      ...(Array.isArray(filters) ? { filters } : {}),
    })
    return data?.data || { runs: [], description: '' }
  },
}

export default reportScheduleService
